/**
 * THE ONE SEAM AUTH LIVES BEHIND (AU-D13).
 *
 * Everything above this module — the client's `getToken` and `getTenantKey`, the cache clear on
 * identity change, the session gate — reads `useSession()` / `currentSession()`. Nothing above
 * this module touches MSAL, and nothing above it reads `uiStore.tenantKey`.
 *
 * A session carries two things, not one: **who** (the signed-in account, whose access token
 * goes out as the bearer) and **where** (the tenant key, sent as `X-LL-Tenant`, decision F-D3).
 * They are separate because they change separately — a person signs in once and may switch
 * tenants several times — and because only one of them is a credential. Selecting a tenant
 * grants nothing: the API reads the principal's membership in the selected tenant on every
 * request and refuses outright when there is none. That is what makes it safe for the tenant key
 * to live in device-local storage while the token does not.
 *
 * **Who** is MSAL (`@azure/msal-browser`), redirect flow, token cache in `sessionStorage`. The
 * bundle carries no authority and no client id: one deployment serves every tenant, so which
 * identity provider to send someone to is only known once discovery has seen their address
 * (`POST /public/tenants/discover`). The option they chose is kept in `sessionStorage` beside
 * MSAL's own cache, because the page that comes back from the identity provider is a fresh page
 * load and has to rebuild the same `PublicClientApplication` to read the answer — and so does
 * every reload after it.
 */
import { useSyncExternalStore } from "react";
import {
  BrowserCacheLocation,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type RedirectRequest,
} from "@azure/msal-browser";

import type { components } from "./client";
import { useUiStore } from "../store/uiStore";

/**
 * One sign-in option as discovery returns it. `domainHint` is intersected in until the generated
 * `SignInOption` carries it (the field is in the API's model; the intersection is harmless once
 * it is regenerated).
 */
export type SignInOption = components["schemas"]["SignInOption"] & {
  domainHint?: string | null;
};

/** What this module keeps of the chosen option: exactly what rebuilding the client needs. */
interface StoredOption {
  authority: string;
  clientId: string;
  scopes: string[];
  domainHint: string | null;
}

export type SessionStatus = "anonymous" | "active";

/** The signed-in person, as the identity provider named them. Shown, never authorised on. */
export interface SessionAccount {
  name: string | null;
  email: string | null;
}

export interface Session {
  status: SessionStatus;
  /** A current access token for the API, renewed silently; "" when anonymous. */
  getToken: () => Promise<string>;
  account: SessionAccount | null;
  /** The selected tenant, sent as `X-LL-Tenant`. Null before one is chosen. */
  tenantKey: string | null;
}

/** The chosen option. `sessionStorage`: it lives exactly as long as MSAL's own cache does. */
const OPTION_KEY = "levelload.signIn";
/** Where to go once the identity provider sends the browser back. Consumed by `completeSignIn`. */
const RETURN_KEY = "levelload.signIn.returnTo";

export const CALLBACK_PATH = "/auth/callback";

/* -------------------------------------------------------------- storage */

function storage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function readStoredOption(): StoredOption | null {
  const raw = storage()?.getItem(OPTION_KEY);
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<StoredOption>;
    if (typeof value.authority !== "string" || typeof value.clientId !== "string") return null;
    return {
      authority: value.authority,
      clientId: value.clientId,
      scopes: Array.isArray(value.scopes) ? value.scopes : [],
      domainHint: value.domainHint ?? null,
    };
  } catch {
    return null;
  }
}

function writeStoredOption(option: StoredOption | null): void {
  const store = storage();
  if (!store) return;
  if (option) store.setItem(OPTION_KEY, JSON.stringify(option));
  else store.removeItem(OPTION_KEY);
}

function writeReturnTo(path: string | null): void {
  const store = storage();
  if (!store) return;
  if (path) store.setItem(RETURN_KEY, path);
  else store.removeItem(RETURN_KEY);
}

function takeReturnTo(): string | null {
  const store = storage();
  const value = store?.getItem(RETURN_KEY) ?? null;
  store?.removeItem(RETURN_KEY);
  return value;
}

/* --------------------------------------------------------------- client */

function origin(): string {
  return typeof window === "undefined" ? "" : window.location.origin;
}

/**
 * External ID authorities (`https://<tenant>.ciamlogin.com/...`) are not in MSAL's built-in
 * cloud list, so the host is named as known; MSAL then trusts the authority's own metadata
 * rather than trying to validate it against `login.microsoftonline.com`.
 *
 * External ID also reports its OIDC `issuer` on a host named by the tenant's GUID
 * (`https://<tenant-guid>.ciamlogin.com/<tenant-guid>/v2.0`) even when the authority is named by
 * its subdomain. MSAL v5 refuses metadata whose issuer host it was not told about
 * (`endpoints_resolution_error`), so when the authority path carries that GUID the GUID host is
 * named as known too. (The sign-in binding stores the GUID form, which also satisfies MSAL's own
 * CIAM issuer rule; this is the second line of defence for the bare-subdomain form.)
 */
export function knownAuthoritiesFor(authority: string): string[] {
  try {
    const url = new URL(authority);
    if (!url.host.endsWith(".ciamlogin.com")) return [];
    const hosts = [url.host];
    const first = url.pathname.split("/").find((segment) => segment.length > 0);
    if (first && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(first)) {
      hosts.push(`${first.toLowerCase()}.ciamlogin.com`);
    }
    return hosts;
  } catch {
    return [];
  }
}

function createClient(option: StoredOption): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: option.clientId,
      authority: option.authority,
      knownAuthorities: knownAuthoritiesFor(option.authority),
      // One redirect URI for every flow, so every answer lands on the one route that reads it.
      redirectUri: `${origin()}${CALLBACK_PATH}`,
    },
    // Not localStorage (AU-D13): a token should not outlive the tab it was issued to.
    cache: { cacheLocation: BrowserCacheLocation.SessionStorage },
  });
}

interface AuthState {
  client: PublicClientApplication | null;
  option: StoredOption | null;
  account: AccountInfo | null;
}

let auth: AuthState = { client: null, option: null, account: null };
let completion: Promise<CompletedSignIn | null> | null = null;

const authListeners = new Set<() => void>();
const notifyAuth = () => authListeners.forEach((l) => l());

function setAuth(next: AuthState): void {
  const changed = next.account?.homeAccountId !== auth.account?.homeAccountId;
  auth = next;
  if (changed) notifyAuth();
}

function pickAccount(client: PublicClientApplication): AccountInfo | null {
  return client.getActiveAccount() ?? client.getAllAccounts()[0] ?? null;
}

/**
 * Rebuild the client from the stored option and pick up the account MSAL already holds. Called
 * once by `main.tsx` before the first render, so the gate never sees a signed-in person as
 * anonymous for a frame. Safe to call again; each call rereads storage.
 */
export async function startSession(): Promise<void> {
  completion = null;
  const option = readStoredOption();
  if (!option) {
    setAuth({ client: null, option: null, account: null });
    return;
  }
  try {
    const client = createClient(option);
    await client.initialize();
    setAuth({ client, option, account: pickAccount(client) });
  } catch (error) {
    // A stored option MSAL cannot build a client from (a bad authority, a storage quirk) is
    // a signed-out session, not a broken app.
    console.error("Could not restore the sign-in session.", error);
    writeStoredOption(null);
    setAuth({ client: null, option: null, account: null });
  }
}

function redirectRequest(option: StoredOption): RedirectRequest {
  return {
    scopes: option.scopes,
    redirectUri: `${origin()}${CALLBACK_PATH}`,
    // No `loginHint`, deliberately. External ID reports AADSTS165000 on a cold session when
    // `login_hint` is sent to a federated custom OpenID Connect provider (Microsoft Q&A, found
    // 2026-10-07), and the address has already been used to choose the option. The cost is that
    // the provider asks for the address again.
    // `domainHint` is MSAL's name for the `domain_hint` query parameter.
    ...(option.domainHint ? { domainHint: option.domainHint } : {}),
  };
}

/**
 * Send the browser to the identity provider for `option`. Never resolves in practice: the page
 * navigates away. `returnTo` is where to land afterwards (the gate's pending path).
 */
export async function beginSignIn(
  option: SignInOption,
  email: string,
  returnTo: string | null = null,
): Promise<void> {
  const stored: StoredOption = {
    authority: option.authority,
    clientId: option.clientId,
    scopes: option.scopes ?? [],
    domainHint: option.domainHint ?? null,
  };
  writeStoredOption(stored);
  writeReturnTo(returnTo);
  completion = null;
  const client = createClient(stored);
  await client.initialize();
  setAuth({ client, option: stored, account: null });
  await client.loginRedirect(redirectRequest(stored));
}

export interface CompletedSignIn {
  account: SessionAccount;
  /** The path the person was on before the redirect, when one was kept. */
  returnTo: string | null;
}

/**
 * Read the identity provider's answer on `/auth/callback`. Null when there is none to read
 * (no stored option, or a visit to the callback with nothing in the URL and no account).
 *
 * Memoised per page load: React's strict mode runs the callback's effect twice, and the
 * response in the URL can be redeemed only once.
 */
export function completeSignIn(): Promise<CompletedSignIn | null> {
  completion ??= (async () => {
    if (!auth.client) await startSession();
    const { client, option } = auth;
    if (!client || !option) return null;
    const result = await client.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    const account = result?.account ?? pickAccount(client);
    if (!account) return null;
    client.setActiveAccount(account);
    setAuth({ client, option, account });
    return { account: accountView(account), returnTo: takeReturnTo() };
  })();
  return completion;
}

async function redirectForToken(returnTo: string | null): Promise<void> {
  const { client, option, account } = auth;
  if (!client || !option || !account) return;
  writeReturnTo(returnTo);
  await client.acquireTokenRedirect({ ...redirectRequest(option), account });
}

function here(): string {
  if (typeof window === "undefined") return "/";
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

/**
 * A current access token. MSAL answers from its cache and renews silently; when the provider
 * needs the person back (`interaction_required`: an expired session, new consent), the page is
 * redirected there and this throws, so the request that asked fails rather than going out bare.
 */
async function acquireToken(): Promise<string> {
  const { client, option, account } = auth;
  if (!client || !option || !account) return "";
  try {
    const result = await client.acquireTokenSilent({ account, scopes: option.scopes });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) await redirectForToken(here());
    throw error;
  }
}

export type Renewal = "renewed" | "redirecting" | "signed-out";

/**
 * After a 401: one forced silent renewal. `renewed` means retrying is worth it; `redirecting`
 * means the provider needs the person and the page is leaving; `signed-out` means there is no
 * account to renew, and sign-in starts again from `/login`.
 */
export async function renewSession(returnTo: string = here()): Promise<Renewal> {
  const { client, option, account } = auth;
  if (!client || !option || !account) return "signed-out";
  try {
    await client.acquireTokenSilent({ account, scopes: option.scopes, forceRefresh: true });
    return "renewed";
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await redirectForToken(returnTo);
      return "redirecting";
    }
    return "signed-out";
  }
}

/* -------------------------------------------------------------- session */

function accountView(account: AccountInfo): SessionAccount {
  const claimed = account.idTokenClaims?.["email"];
  return {
    name: account.name || null,
    email: (typeof claimed === "string" && claimed) || account.username || null,
  };
}

function sessionFor(account: AccountInfo | null, tenantKey: string | null): Session {
  return {
    status: account ? "active" : "anonymous",
    getToken: acquireToken,
    account: account ? accountView(account) : null,
    tenantKey,
  };
}

/** Non-React read of the current session. */
export function currentSession(): Session {
  return sessionFor(auth.account, useUiStore.getState().tenantKey);
}

const identityOf = () =>
  `${auth.account?.homeAccountId ?? ""}\u0000${useUiStore.getState().tenantKey ?? ""}`;

function subscribeAll(onChange: () => void): () => void {
  authListeners.add(onChange);
  const unsubStore = useUiStore.subscribe(onChange);
  return () => {
    authListeners.delete(onChange);
    unsubStore();
  };
}

/**
 * Non-React subscription to identity OR tenant changes. Fires only when one of them actually
 * changes, so callers (the cache clear) do not need to diff.
 *
 * The tenant belongs in here with the identity, not beside it: switching tenants has exactly the
 * same consequence as switching user — every cached row was fetched for the other one. Every
 * query key is `['ll', tenantKey, ...]`, so a stale entry could not be *read* by mistake, but it
 * would sit in memory until it expired, and a refetch-on-mount race could still paint the
 * previous tenant's rows for a frame. Clearing is cheaper than reasoning about that.
 */
export function subscribeSession(cb: (session: Session) => void): () => void {
  let last = identityOf();
  return subscribeAll(() => {
    const next = identityOf();
    if (next === last) return;
    last = next;
    cb(currentSession());
  });
}

// `useSyncExternalStore` compares snapshots by identity, so this must be a stable primitive
// rather than a fresh object each call -- returning `{account, tenantKey}` would re-render on
// every store write, including a column-layout change.
const getServerSnapshot = () => "\u0000";

/** React read of the session. The only thing components ever use for identity. */
export function useSession(): Session {
  useSyncExternalStore(subscribeAll, identityOf, getServerSnapshot);
  return currentSession();
}

/** Choose the tenant every subsequent request is for. Clears the cache via `subscribeSession`. */
export function selectTenant(tenantKey: string | null): void {
  useUiStore.getState().setTenantKey(tenantKey);
}

/**
 * Forget the session on this device without leaving the page: the account, the tenant and the
 * stored option, and MSAL's cached tokens. The cache clear rides `subscribeSession` like any
 * other identity change.
 *
 * The tenant goes with the identity: the next person at this device must choose again, and a
 * tenant left selected would be the one thing on screen that outlived the session it belonged
 * to.
 */
export async function endSession(): Promise<void> {
  const { client, account } = auth;
  writeStoredOption(null);
  writeReturnTo(null);
  completion = null;
  selectTenant(null);
  setAuth({ client: null, option: null, account: null });
  if (client) {
    try {
      await client.clearCache(account ? { account } : undefined);
    } catch (error) {
      console.error("Could not clear the sign-in cache.", error);
    }
  }
}

/**
 * Sign out: `endSession`, then a full load of `/login`, so nothing held in memory — the query
 * cache, a pending path — survives into the next person's session either.
 *
 * Local only: the identity provider's own session is left alone (ending it would need a
 * post-logout redirect URI registered per app registration).
 */
export async function signOut(): Promise<void> {
  await endSession();
  if (typeof window !== "undefined") window.location.assign("/login");
}
